import React, { useState, useEffect } from 'react';
import { Flame, UserCheck, ShieldCheck, Layers, MessageCircle, ExternalLink, Play, ArrowUpRight } from 'lucide-react';
import { useSite } from '../../context/SiteContext';
import { useFilter } from '../../context/FilterContext';
import { resolveMediaUrl } from '../admin/MediaUpload';

const DEFAULT_B2C_EXCHANGES = [
  { id: 'b2c_1', name: 'Lotus 1', rate: '16%', type: 'B2C', tags: 'Live Cricket, Casino & Sportsbook', logo: '', link: 'https://lotusdemo1.com/', popular: true },
  { id: 'b2c_2', name: 'Lotus 2', rate: '16%', type: 'B2C', tags: 'High Speed Sports Exchange', logo: '', link: 'https://lotusdemo2.com/' },
  { id: 'b2c_3', name: 'SouthWin', rate: '16%', type: 'B2C', tags: 'Premium Sportsbook & Casino', logo: '', link: 'https://southwindemo.com/' },
  { id: 'b2c_4', name: 'UltraWin 1', rate: '16%', type: 'B2C', tags: 'Instant IDs & Live Odds', logo: '', link: 'https://ultrawindemo1.com/' },
  { id: 'b2c_5', name: '99Exch', rate: '16%', type: 'B2C', tags: 'Top Indian Sports Exchange', logo: '', link: 'https://99exchdemo.com/', popular: true },
  { id: 'b2c_6', name: 'SkyExch 1', rate: '16%', type: 'B2C', tags: 'Premium Betting & Casino', logo: '', link: 'https://skyexchdemo1.com/', popular: true },
  { id: 'b2c_7', name: 'WinBuzz 1', rate: '16%', type: 'B2C', tags: 'Fastest Live Betting Platform', logo: '', link: 'https://winbuzzdemo1.com/', popular: true },
  { id: 'b2c_8', name: 'Tiger 1', rate: '16%', type: 'B2C', tags: 'Multi-market Live Exchange', logo: '', link: 'https://tigerdemo1.com/' },
  { id: 'b2c_9', name: '20Wicket', rate: '16%', type: 'B2C', tags: 'Dedicated Cricket & Sportsbook', logo: '', link: 'https://20wicketdemo.com/' },
  { id: 'b2c_10', name: 'FairPlay', rate: '16%', type: 'B2C', tags: 'World Class Live Casino & Exchange', logo: '', link: 'https://fairplaydemo.com/', popular: true },
  { id: 'b2c_11', name: '11xPlay', rate: '16%', type: 'B2C', tags: '100+ Live Casino Games & Odds', logo: '', link: 'https://11xplaydemo.com/' },
  { id: 'b2c_12', name: 'ReddyBook 1', rate: '16%', type: 'B2C', tags: 'Leading Club & Sports Book', logo: '', link: 'https://reddybookdemo1.com/', popular: true },
  { id: 'b2c_13', name: 'AllPanel', rate: '16%', type: 'B2C', tags: 'Master, Admin & User Panel', logo: '', link: 'https://allpaneldemo.com/' },
  { id: 'b2c_14', name: 'Laser 247', rate: '16%', type: 'B2C', tags: '24/7 Cricket & Table Games', logo: '', link: 'https://laserdemo.co/' },
  { id: 'b2c_15', name: 'Deal Demo', rate: '16%', type: 'B2C', tags: 'Direct Line & Coin Deals', logo: '', link: 'https://dealdemo.co/' },
  { id: 'b2c_16', name: 'Reddy South', rate: '16%', type: 'B2C', tags: 'Southern Club & High Roller Sportsbook', logo: '', link: 'https://reddysouth.com/' },
  { id: 'b2c_17', name: 'All Khiladi', rate: '16%', type: 'B2C', tags: 'The Ultimate Sports Platform', logo: '', link: 'https://allkhiladi.com/' }
];

const DEFAULT_B2B_EXCHANGES = [
  { id: 'b2b_1', name: '4Wicket', rate: '16%', type: 'B2B', tags: 'Distributor & Master Super Admin Portal', logo: '', link: 'https://www.4wicket.com/', popular: true },
  { id: 'b2b_2', name: 'RadheExch 247', rate: '16%', type: 'B2B', tags: 'Radhe 247 Agent & Master Network Panel', logo: '', link: 'https://radheexch247.com/' },
  { id: 'b2b_3', name: 'AllPanel Buzz', rate: '16%', type: 'B2B', tags: 'AllPanel Master, Agent & Client Portal', logo: '', link: 'https://allpanel.buzz/', popular: true },
  { id: 'b2b_4', name: 'TigerExch Pro', rate: '16%', type: 'B2B', tags: 'Tiger Pro Master Backend & Risk Management', logo: '', link: 'https://tigerexch.pro' }
];

export default function ExchangeListings() {
  const { data } = useSite();
  const { category, query } = useFilter();
  const el = data.exchangeListings || {};
  const visible = (data.sectionVisibility || {}).exchanges !== false;
  const [activeTab, setActiveTab] = useState('all');

  // React to navbar GET B2B / GET B2C clicks
  useEffect(() => {
    if (category === 'b2b') setActiveTab('b2b');
    else if (category === 'b2c') setActiveTab('b2c');
  }, [category]);

  if (!visible || el.enabled === false) return null;

  // Extract from backend config if available
  const configuredTypes = (el.types && el.types.length > 0) ? el.types : null;
  const b2bType = configuredTypes?.find(t => t.id === 'b2b' || t.label?.toLowerCase()?.includes('b2b'));
  const b2cType = configuredTypes?.find(t => t.id === 'b2c' || t.label?.toLowerCase()?.includes('b2c'));

  const b2bList = (b2bType && b2bType.items && b2bType.items.length >= 4)
    ? b2bType.items.map(it => ({ ...it, type: 'B2B' }))
    : DEFAULT_B2B_EXCHANGES;

  const b2cList = (b2cType && b2cType.items && b2cType.items.length >= 10)
    ? b2cType.items.map(it => ({ ...it, type: 'B2C' }))
    : DEFAULT_B2C_EXCHANGES;

  // B2B first, then B2C
  const allList = [...b2bList, ...b2cList];
  const hasExplicitPopular = allList.some(it => typeof it.popular === 'boolean');
  const popularList = hasExplicitPopular
    ? allList.filter(it => !!it.popular)
    : allList.filter(it => it.popular || ['b2b_1', 'b2b_3', 'b2c_1', 'b2c_5', 'b2c_6', 'b2c_7', 'b2c_10'].includes(it.id));

  const currentItems = activeTab === 'popular'
    ? popularList
    : activeTab === 'b2b'
      ? b2bList
      : activeTab === 'b2c'
        ? b2cList
        : allList;

  // Search by text on page like a finder
  const q = (query || '').trim().toLowerCase();
  const displayItems = q
    ? allList.filter(matches)
    : currentItems;

  const whatsapp = el.whatsapp || data.whatsappLink || '';

  const accentColor = el.accentColor || '#ccc77d';
  const b2cColor = el.b2cColor || '#ccc77d';
  const b2bColor = el.b2bColor || '#ccc77d';
  const popularColor = el.popularColor || '#ccc77d';
  const rateColor = el.rateColor || '#ccc77d';

  return (
    <div id="exchanges" className="rounded-xl bg-zinc-950/40 border border-zinc-900 overflow-hidden my-4" data-testid="exchanges-section">
      {/* Header Bar matching Get ID UI styling */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 bg-gradient-to-r from-[#ccc77d]/15 to-transparent border-b border-zinc-900">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-[#ccc77d] text-black flex items-center justify-center">
            <Layers size={12} className="text-black" />
          </span>
          <span className="text-white font-bold text-sm md:text-base tracking-wide uppercase">
            {el.title || 'EXCHANGES AVAILABLE'}
          </span>
        </div>

        {/* Section Selector Tabs: 1. All -> 2. Popular -> 3. B2B -> 4. B2C */}
        <div className="flex items-center gap-1.5 p-1 bg-zinc-900/90 border border-zinc-800 rounded-xl overflow-x-auto no-scrollbar max-w-full">
          {/* 1. All Button */}
          <button
            type="button"
            onClick={() => setActiveTab('all')}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold transition-all whitespace-nowrap border ${
              activeTab === 'all'
                ? 'bg-[#ccc77d] text-black border-[#ccc77d] shadow-md font-extrabold'
                : 'text-white/80 hover:text-white hover:bg-white/10 border-transparent'
            }`}
          >
            <Layers size={12} className={activeTab === 'all' ? 'text-black' : 'text-white'} />
            <span>All ({allList.length})</span>
          </button>

          {/* 2. Popular Button */}
          <button
            type="button"
            onClick={() => setActiveTab('popular')}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold transition-all whitespace-nowrap border ${
              activeTab === 'popular'
                ? 'bg-[#ccc77d] text-black border-[#ccc77d] shadow-md font-extrabold'
                : 'text-white/80 hover:text-white hover:bg-white/10 border-transparent'
            }`}
          >
            <Flame size={12} className={activeTab === 'popular' ? 'text-black fill-black' : 'text-white fill-white'} />
            <span>Popular ({popularList.length})</span>
          </button>

          {/* 3. B2B Button */}
          <button
            type="button"
            onClick={() => setActiveTab('b2b')}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold transition-all whitespace-nowrap border ${
              activeTab === 'b2b'
                ? 'bg-[#ccc77d] text-black border-[#ccc77d] shadow-md font-extrabold'
                : 'text-white/80 hover:text-white hover:bg-white/10 border-transparent'
            }`}
          >
            <ShieldCheck size={12} className={activeTab === 'b2b' ? 'text-black' : 'text-white'} />
            <span>B2B ({b2bList.length})</span>
          </button>

          {/* 4. B2C Button */}
          <button
            type="button"
            onClick={() => setActiveTab('b2c')}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold transition-all whitespace-nowrap border ${
              activeTab === 'b2c'
                ? 'bg-[#ccc77d] text-black border-[#ccc77d] shadow-md font-extrabold'
                : 'text-white/80 hover:text-white hover:bg-white/10 border-transparent'
            }`}
          >
            <UserCheck size={12} className={activeTab === 'b2c' ? 'text-black' : 'text-white'} />
            <span>B2C ({b2cList.length})</span>
          </button>
        </div>
      </div>

      {/* DEDICATED POPULAR EXCHANGES SECTION BLOCK */}
      {popularList.length > 0 && (activeTab === 'all' || activeTab === 'popular') && !q && (
        <div className="p-4 bg-gradient-to-b from-amber-500/10 via-zinc-950/80 to-zinc-950 border-b border-zinc-900" data-testid="popular-exchanges-section">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center border border-amber-500/40">
                <Flame size={13} className="fill-amber-400 text-amber-400" />
              </span>
              <span className="text-amber-300 font-extrabold text-xs md:text-sm tracking-wider uppercase">
                🔥 POPULAR EXCHANGES
              </span>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30">
                {popularList.length} Featured
              </span>
            </div>
            {activeTab === 'all' && (
              <button
                type="button"
                onClick={() => setActiveTab('popular')}
                className="text-xs text-amber-400 hover:text-amber-300 font-semibold hover:underline"
              >
                View All ({popularList.length}) →
              </button>
            )}
          </div>

          {/* Featured Cards Grid for Popular Section */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {popularList.slice(0, activeTab === 'popular' ? popularList.length : 6).map((m, i) => {
              const isB2B = (m.type || '').toUpperCase() === 'B2B';
              const actionLabel = isB2B ? 'GET B2B' : 'GET B2C';
              const rawWa = (data?.exchangeListings || {}).whatsapp || data?.whatsappLink || (data?.social || {}).whatsapp || '';
              const digits = rawWa.replace(/\D/g, '');
              const tgLink = (data?.social || {}).telegram || data?.telegramLink || '';
              const formattedTg = tgLink ? (tgLink.startsWith('http') ? tgLink : `https://t.me/${tgLink.replace(/^@/, '')}`) : '';

              let actionHref = m.link || '#';
              if (data?.socialVisibility?.whatsapp !== false && digits) {
                actionHref = `https://wa.me/${digits}?text=${encodeURIComponent(`I want ${m.name} (${m.type}) with ${m.rate || '16%'} share rate`.trim())}`;
              } else if (data?.socialVisibility?.telegram !== false && formattedTg) {
                actionHref = formattedTg;
              }

              return (
                <div
                  key={m.id || `pop_${i}`}
                  className="group relative rounded-xl bg-zinc-900/90 p-3.5 border border-amber-500/30 hover:border-amber-500/80 transition-all shadow-md hover:shadow-amber-500/10 flex flex-col justify-between"
                  data-testid={`popular-exchange-card-${m.id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2.5">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-10 h-10 rounded-lg bg-zinc-950 border border-zinc-800 overflow-hidden flex items-center justify-center shrink-0">
                        {m.logo ? (
                          <img src={resolveMediaUrl(m.logo)} alt={m.name} className="max-w-full max-h-full object-contain" />
                        ) : (
                          <span className="text-[#ccc77d] font-bold text-sm">
                            {(m.name || 'E').charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-bold text-sm text-white group-hover:text-amber-300 transition-colors truncate">{m.name}</h4>
                        <span className="text-[11px] text-zinc-400 block truncate">{m.description || m.tags || (isB2B ? 'Master & Admin' : 'Sportsbook')}</span>
                      </div>
                    </div>
                    <span className="px-2 py-0.5 rounded text-[10px] font-extrabold bg-amber-500/20 text-amber-300 border border-amber-500/40 shrink-0">
                      {m.rate || '16%'}
                    </span>
                  </div>

                  <div className="flex items-center justify-between gap-2 pt-2 border-t border-zinc-800/80 mt-1">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-zinc-800 text-zinc-300 uppercase">
                      {isB2B ? 'B2B White Label' : 'B2C Line ID'}
                    </span>
                    <a
                      href={actionHref}
                      target="_blank"
                      rel="noreferrer"
                      className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-extrabold text-xs flex items-center gap-1 shadow-sm transition-all"
                    >
                      <span>{actionLabel}</span>
                      <ArrowUpRight size={13} />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Desktop Column Header without Type: 8 cols Platform, 2 cols Share Rate, 2 cols Action = 12 cols */}
      <div className="hidden md:grid grid-cols-12 items-center px-4 py-2.5 border-b border-zinc-900 bg-zinc-950/60 text-xs font-semibold text-zinc-400">
        <div className="col-span-8">Platform</div>
        <div className="col-span-2 text-center">Share Rate</div>
        <div className="col-span-2 text-right">Action</div>
      </div>

      {/* Rows Container matching Get ID UI divide-y */}
      <div className="divide-y divide-zinc-900">
        {displayItems.length === 0 ? (
          <div className="py-8 text-center text-zinc-400 text-xs">
            No exchanges matching "{query}"
          </div>
        ) : (
          displayItems.map((m, i) => {
            const isB2B = (m.type || '').toUpperCase() === 'B2B';
            return (
              <Row
                key={m.id || i}
                m={{
                  ...m,
                  rate: m.rate || '16%',
                  tags: m.description || m.tags || (isB2B ? 'Master, Admin & Agent Network Portal' : 'Live Cricket, Casino & Sportsbook'),
                  type: isB2B ? 'B2B' : 'B2C'
                }}
                isB2B={isB2B}
                data={data}
              />
            );
          })
        )}
      </div>
    </div>
  );
}

function Row({ m, isB2B, data }) {
  const sv = data?.socialVisibility || {};
  const isWaEnabled = sv.whatsapp !== false;
  const isTgEnabled = sv.telegram !== false;

  const rawWa = (data?.exchangeListings || {}).whatsapp || data?.whatsappLink || (data?.social || {}).whatsapp || '';
  const digits = rawWa.replace(/\D/g, '');

  const tgLink = (data?.social || {}).telegram || data?.telegramLink || '';
  const formattedTg = tgLink ? (tgLink.startsWith('http') ? tgLink : `https://t.me/${tgLink.replace(/^@/, '')}`) : '';

  // Priority: WhatsApp (if enabled) > Telegram (if WA disabled & TG enabled) > Direct Link
  let actionHref = m.link || '#';
  if (isWaEnabled && digits) {
    actionHref = `https://wa.me/${digits}?text=${encodeURIComponent(`I want ${m.name} (${m.type}) with ${m.rate || '16%'} share rate`.trim())}`;
  } else if (isTgEnabled && formattedTg) {
    actionHref = formattedTg;
  }

  const actionLabel = isB2B ? 'GET B2B' : 'GET B2C';

  return (
    <div
      className="match-row grid grid-cols-12 items-center px-3 md:px-4 py-3 gap-2 hover:bg-zinc-900/40 transition"
      data-testid={`exchange-row-${m.id}`}
    >
      {/* Col 8 on Desktop, full row on Mobile - always same line row */}
      <div className="col-span-12 md:col-span-8 flex items-center justify-between md:justify-start gap-2.5 sm:gap-3 min-w-0">
        <div className="flex items-center gap-2.5 sm:gap-3 min-w-0 flex-1">
          {/* Logo matching Get ID style */}
          <div className="w-10 h-10 rounded-md bg-zinc-900 border border-zinc-800 overflow-hidden flex items-center justify-center shrink-0">
            {m.logo ? (
              <img src={resolveMediaUrl(m.logo)} alt={m.name} className="max-w-full max-h-full object-contain" />
            ) : (
              <span className="text-[#ccc77d] font-bold text-sm">
                {(m.name || 'E').charAt(0).toUpperCase()}
              </span>
            )}
          </div>

          {/* Name + Arrow on Title + Subtitle */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              {/* Visit site is like arrow on title of site */}
              <a
                href={m.link || '#'}
                target="_blank"
                rel="noreferrer"
                title={`Visit ${m.name}`}
                className="text-white hover:text-[#ccc77d] font-semibold text-sm inline-flex items-center gap-1 group truncate transition"
              >
                <span className="truncate">{m.name}</span>
                <ArrowUpRight size={14} className="shrink-0 text-white/70 group-hover:text-white transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>

            <div className="text-zinc-400 text-[11px] truncate mt-0.5">
              {m.tags}
            </div>
          </div>
        </div>

        {/* Mobile: Share Rate & Action Button in the SAME line row with matching round shape */}
        <div className="md:hidden flex items-center gap-1.5 shrink-0">
          <span
            className="h-8 px-2.5 rounded-full text-xs font-black font-mono tabular-nums border border-[#ccc77d] bg-[#ccc77d] text-black inline-flex items-center justify-center shadow-xs"
            data-testid={`mobile-share-rate-${m.id}`}
          >
            {m.rate || '16%'}
          </span>
          <a
            href={actionHref}
            target="_blank"
            rel="noreferrer"
            className="h-8 px-3 rounded-full bg-[#ccc77d] hover:bg-[#d8d387] text-black text-xs font-black flex items-center justify-center whitespace-nowrap shadow-xs transition active:scale-95"
          >
            {actionLabel}
          </a>
        </div>
      </div>

      {/* Col 2: Share Rate on Desktop (Matching round shape like GET B2C / GET B2B) */}
      <div className="hidden md:flex col-span-2 items-center justify-center">
        <span className="h-8 px-3.5 rounded-full text-xs font-black font-mono tabular-nums border border-[#ccc77d] bg-[#ccc77d] text-black inline-flex items-center justify-center shadow-xs">
          {m.rate || '16%'}
        </span>
      </div>

      {/* Col 2: Desktop Action Button (Matching round shape) */}
      <div className="hidden md:flex col-span-2 items-center justify-end">
        <a
          href={actionHref}
          target="_blank"
          rel="noreferrer"
          className="h-8 px-3.5 rounded-full bg-[#ccc77d] hover:bg-[#d8d387] text-black text-xs font-black flex items-center justify-center whitespace-nowrap shadow-xs transition active:scale-95"
        >
          {actionLabel}
        </a>
      </div>
    </div>
  );
}
